import type { ImageSource } from 'pixi.js';
import { IS_IMAGE_DECODER_SUPPORTED } from '../../constants';

class IndexedPromise<T> {
    index: number;
    #promise: Promise<void>;
    result?: T;
    #isDestroyed: boolean;
    #destroyResultFunc?: (result?: T) => void;

    /**
     * @param destroyResultFunc Called when the promise finishes but destroy() was already called.
     */
    constructor(index: number, promise: Promise<T>, destroyResultFunc?: (result?: T) => void) {
        this.index = index;
        this.result = undefined;
        this.#isDestroyed = false;

        this.#destroyResultFunc = destroyResultFunc;
        this.#promise = promise
            .then((r) => {
                this.result = r;
                if (this.#isDestroyed && this.#destroyResultFunc) {
                    this.#destroyResultFunc(r);
                }
            })
            .catch((e) => {
                if (this.isDestroyed) {
                    return;
                }

                throw new Error(`Failed to resolve promise. ${e}`);
            });
    }

    async awaitPromise() {
        await this.#promise;
    }

    get isDestroyed() {
        return this.#isDestroyed;
    }

    destroy() {
        this.#isDestroyed = true;
        if (this.result !== undefined) {
            // destroy immediately since the promise is already finished.
            if (this.#destroyResultFunc) {
                this.#destroyResultFunc(this.result);
            }
        }
        this.result = undefined;
    }
}

const SEEK_AHEAD_FRAMES_COUNT = 5;

export class WebpResource {
    #sourceUrl: string;
    #imageDecoder: ImageDecoder | null;
    /**
     * A list of timestamps of webp frames in ms.
     */
    #frameTimestamps: Array<number> | null;
    /**
     * This is a fallback if the browser doesn't support animated webp.
     * This is only populated if the browser doesn't support animated webp (e.g., Safari).
     */
    #wrappedImage: HTMLImageElement | null;
    #sourceDuration: number;
    #width: number;
    #height: number;
    #destroyed: boolean;
    #loaded: boolean;

    /**
     * It is guaranteed that the index in each IndexedPromise corresponds to the
     * exact timestamp of that frame in #frameTimestamps.
     */
    #seekAheadBuffer: IndexedPromise<ImageDecodeResult>[] | null;

    #currentFrame: ImageSource | null;

    #setCurrentFrameAndDestroyPrevious(newValue: ImageSource | null) {
        // Only swap if there is a new frame, otherwise keep the old one.
        if (newValue === this.#currentFrame) return;
        if (newValue) {
            if (this.#currentFrame && this.#currentFrame instanceof VideoFrame) {
                this.#currentFrame.close();
            }
            this.#currentFrame = newValue;
        }
    }

    constructor(sourceUrl: string, sourceDuration: number | null) {
        this.#sourceUrl = sourceUrl;
        this.#imageDecoder = null;
        this.#frameTimestamps = null;
        this.#wrappedImage = null;
        this.#sourceDuration = sourceDuration || 0;
        this.#width = -1;
        this.#height = -1;
        this.#destroyed = false;
        this.#loaded = false;
        this.#currentFrame = null;
        this.#seekAheadBuffer = null;
    }

    get source(): ImageSource | null {
        return this.#currentFrame;
    }

    get width(): number {
        return this.#width;
    }

    get height(): number {
        return this.#height;
    }

    get destroyed() {
        return this.#destroyed;
    }

    get valid(): boolean {
        return !this.destroyed && this.#loaded;
    }

    get #isAnimated(): boolean {
        return (
            IS_IMAGE_DECODER_SUPPORTED &&
            this.#frameTimestamps !== null &&
            this.#frameTimestamps.length > 0
        );
    }

    get framesCount() {
        return this.#isAnimated ? this.#frameTimestamps?.length || 0 : 1;
    }

    get sourceDuration() {
        return this.#sourceDuration;
    }

    get #seekAheadFramesCount() {
        return Math.min(SEEK_AHEAD_FRAMES_COUNT, this.framesCount);
    }

    static #microsToMillis(microseconds: number) {
        return microseconds / 1e3;
    }

    static #millisToMicros(milliseconds: number) {
        return milliseconds * 1e3;
    }

    static #closeVideoFrame(decodeResult?: ImageDecodeResult) {
        decodeResult?.image?.close();
    }

    #decodeFrame(index: number): IndexedPromise<ImageDecodeResult> {
        if (!this.#imageDecoder) {
            throw new Error('decodeFrame is called before imageDecoder is initialized.');
        }
        const promise = this.#imageDecoder.decode({ frameIndex: index });
        const indexedPromise = new IndexedPromise<ImageDecodeResult>(
            index,
            promise,
            WebpResource.#closeVideoFrame,
        );
        return indexedPromise;
    }

    async load(): Promise<WebpResource> {
        try {
            if (IS_IMAGE_DECODER_SUPPORTED) {
                const response = await fetch(this.#sourceUrl);
                const arrayBuffer = await response.arrayBuffer();

                this.#imageDecoder = new window.ImageDecoder({
                    type: 'image/webp',
                    data: arrayBuffer,
                    preferAnimation: true,
                });

                await this.#imageDecoder.tracks.ready;

                const track = this.#imageDecoder.tracks.selectedTrack;
                if (!track) {
                    throw new Error('Failed to load image decoder track.');
                }
                this.#frameTimestamps = [];
                for (let i = 0; i < track.frameCount; i++) {
                    const result = await this.#imageDecoder.decode({ frameIndex: i });
                    this.#frameTimestamps.push(
                        WebpResource.#microsToMillis(result.image.timestamp),
                    );
                    if (i === 0) {
                        this.#width = result.image.displayWidth;
                        this.#height = result.image.displayHeight;
                        this.#setCurrentFrameAndDestroyPrevious(result.image);
                    } else {
                        result.image.close();
                    }
                }
                if (this.#frameTimestamps.length > 1) {
                    const lastTimestamp = this.#frameTimestamps.at(-1)!;
                    const deltaTime = lastTimestamp - this.#frameTimestamps.at(-2)!;
                    this.#sourceDuration = lastTimestamp + deltaTime;
                } else {
                    this.#sourceDuration = 0; // only a single frame
                }
                this.#seekAheadBuffer = [];
            } else {
                this.#frameTimestamps = null;
                this.#imageDecoder = null;
                this.#sourceDuration = 0.0;
                this.#wrappedImage = new Image();
                await new Promise<void>((resolve, reject) => {
                    this.#wrappedImage!.onload = () => resolve();
                    this.#wrappedImage!.onerror = () => reject(new Error('Failed to load image'));
                    this.#wrappedImage!.src = this.#sourceUrl;
                });
                this.#width = this.#wrappedImage.width;
                this.#height = this.#wrappedImage.height;
                this.#setCurrentFrameAndDestroyPrevious(this.#wrappedImage);
            }
            this.#loaded = true;
        } catch (error) {
            console.error('Failed to load WebP animation:', error);
            this.destroy();
            throw error;
        }
        return this;
    }

    /**
     * This function sets currentFrame to be the closest decoded frame at or before
     * the given timestamp. All other frames that occur before the given timestamp
     * are automatically destroyed.
     *
     * If all buffered frames occur before the given time, the last buffered frame
     * will be set as the current frame.
     *
     * This function also starts to seek this.#seekAheadFramesCount frames, starting
     * from the given time. Any frames that start from the given time that are
     * already being buffered (or are done buffering) will be kept (i.e., this
     * function does not request decoding of the same frame multiple times).
     */
    #seekAheadOfTimeAndSetCurrentFrame(time: number) {
        if (!this.#seekAheadBuffer) {
            throw new Error('Cannot seek when the seek ahead buffer is not initialized.');
        }

        const seekStartIndex = this.#timeToFrameIndex(time);
        while (this.#seekAheadBuffer.length > 0) {
            if (this.#seekAheadBuffer[0].index === seekStartIndex) {
                // any items behind this index should be kept in the buffer,
                // since they are consecutive frames.
                break;
            } else {
                // remove the useless buffers
                const indexedPromise = this.#seekAheadBuffer.shift()!;
                if (indexedPromise.result) {
                    this.#setCurrentFrameAndDestroyPrevious(indexedPromise.result.image);
                } else {
                    indexedPromise.destroy();
                }
            }
        }

        while (this.#seekAheadBuffer.length < this.#seekAheadFramesCount) {
            const index = (seekStartIndex + this.#seekAheadBuffer.length) % this.framesCount;
            const indexedPromise = this.#decodeFrame(index);
            this.#seekAheadBuffer.push(indexedPromise);
        }
    }

    async buffer(time: number) {
        if (!this.#isAnimated) {
            return;
        }
        if (!this.#seekAheadBuffer) {
            throw new Error('Cannot seek when the seek ahead buffer is not initialized.');
        }
        this.#seekAheadOfTimeAndSetCurrentFrame(time);

        // await all promises to ensure that the frames are ready immediately
        for (let i = 0; i < this.#seekAheadBuffer.length; i++) {
            const indexedPromise = this.#seekAheadBuffer[i]!;
            await indexedPromise.awaitPromise();
            if (i === 0) {
                this.#setCurrentFrameAndDestroyPrevious(indexedPromise.result!.image);
            }
        }
    }

    /**
     * Recalculates the current frame to the given time in milliseconds. If the browser doesn't support animated webp or if
     * the webp is not animated, this function will set the current frame to the static image representation of the webp.
     */
    seek(time: number) {
        if (this.#isAnimated) {
            this.#seekAheadOfTimeAndSetCurrentFrame(time);
        } else {
            this.#currentFrame = this.#wrappedImage!;
            this.#width = this.#currentFrame.width;
            this.#height = this.#currentFrame.height;
        }
    }

    #timeToFrameIndex(timeInMs: number) {
        if (this.#sourceDuration === 0) {
            return this.framesCount > 0 ? 0 : -1;
        }
        if (!this.#frameTimestamps) {
            return 0;
        }
        timeInMs %= this.#sourceDuration;
        let closestDistance = Infinity;
        let closestInd = -1;
        // If optimization is needed, this can be changed to binary search in the future.
        for (let i = 0; i < this.#frameTimestamps.length; i++) {
            const distanceI = timeInMs - this.#frameTimestamps[i]!;
            if (distanceI < 0) break; // we moved passed the frame of interest
            if (distanceI <= closestDistance) {
                closestDistance = distanceI;
                closestInd = i;
            }
        }
        return closestInd === -1 ? 0 : closestInd;
    }

    destroy() {
        this.#destroyed = true;
        if (this.#imageDecoder !== null) {
            this.#imageDecoder.close();
            this.#imageDecoder = null;
        }
        if (this.#wrappedImage !== null) {
            this.#wrappedImage.remove();
            this.#wrappedImage = null;
        }
        if (this.#seekAheadBuffer) {
            this.#seekAheadBuffer.forEach((indexedPromise) => {
                indexedPromise.destroy();
            });
            this.#seekAheadBuffer = null;
        }
        this.#frameTimestamps = null;
    }

    static isWebp(sourceUrl: string | null) {
        return sourceUrl?.toLowerCase()?.endsWith('.webp') || false;
    }
}
